import axios, { AxiosResponse } from 'axios';
import { 
  User, 
  Product, 
  Order, 
  Category,
  AuthResponse, 
  ApiResponse, 
  PaginatedResponse,
  DashboardStats,
  MonthlyRevenue 
} from '../types/index.ts';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    // Check for customer token first, then admin token
    const customerToken = localStorage.getItem('customer_token');
    const adminToken = localStorage.getItem('admin_token');
    const token = customerToken || adminToken;
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    console.log('API Request:', config.method?.toUpperCase(), config.url, {
      hasToken: !!token,
      tokenType: customerToken ? 'customer' : adminToken ? 'admin' : 'none',
      origin: window.location.origin
    });
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', {
      status: error.response?.status,
      message: error.message,
      url: error.config?.url,
      method: error.config?.method,
      isNetworkError: !error.response
    });
    
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (userData: {
    name: string;
    email: string;
    password: string;
    phone: string;
    address?: string;
    city?: string;
    pincode?: string;
  }): Promise<AxiosResponse<AuthResponse>> =>
    api.post('/auth/register', userData),

  login: (credentials: {
    email: string;
    password: string;
  }): Promise<AxiosResponse<AuthResponse>> =>
    api.post('/auth/login', credentials),

  getProfile: (): Promise<AxiosResponse<ApiResponse<{ user: User }>>> =>
    api.get('/auth/profile'),

  updateProfile: (userData: {
    name?: string;
    phone?: string;
    address?: string;
    city?: string;
    pincode?: string;
  }): Promise<AxiosResponse<ApiResponse<{ user: User }>>> =>
    api.put('/auth/profile', userData),
};

// Products API
export const productsAPI = {
  getProducts: (params?: {
    type?: string;
    category?: string;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<AxiosResponse<PaginatedResponse<Product>>> =>
    api.get('/products', { params }),

  getProduct: (id: number): Promise<AxiosResponse<ApiResponse<{ product: Product }>>> =>
    api.get(`/products/${id}`),

  getCategories: (): Promise<AxiosResponse<ApiResponse<{ categories: Category[] }>>> =>
    api.get('/products/categories'),
};

// Orders API
export const ordersAPI = {
  createOrder: (orderData: {
    items: Array<{
      productId: number;
      quantity: number;
    }>;
    deliveryAddress: string;
    deliveryCity: string;
    deliveryPincode: string;
    deliveryPhone: string;
    deliveryDate?: string;
    deliveryTime?: string;
    notes?: string;
    paymentMethod?: string;
  }): Promise<AxiosResponse<ApiResponse<{ order: Order }>>> =>
    api.post('/orders', orderData),

  getUserOrders: (params?: {
    page?: number;
    limit?: number;
    status?: string;
  }): Promise<AxiosResponse<PaginatedResponse<Order>>> =>
    api.get('/orders', { params }),

  getOrder: (id: number): Promise<AxiosResponse<ApiResponse<{ order: Order }>>> =>
    api.get(`/orders/${id}`),

  cancelOrder: (id: number): Promise<AxiosResponse<ApiResponse<null>>> =>
    api.put(`/orders/${id}/cancel`),
};

// Cart API
export const cartAPI = {
  getCart: (): Promise<AxiosResponse<ApiResponse<{
    items: Array<{
      id: number;
      userId: number;
      productId: number;
      quantity: number;
      product: Product;
      createdAt: string;
      updatedAt: string;
    }>;
    totalItems: number;
    totalPrice: number;
    totalDeposit: number;
    grandTotal: number;
  }>>> => api.get('/cart'),

  addToCart: (productId: number, quantity: number = 1): Promise<AxiosResponse<ApiResponse<{
    id: number;
    userId: number;
    productId: number;
    quantity: number;
    product: Product;
    createdAt: string;
    updatedAt: string;
  }>>> => api.post('/cart', { productId, quantity }),

  updateCartItem: (productId: number, quantity: number): Promise<AxiosResponse<ApiResponse<{
    id: number;
    userId: number;
    productId: number;
    quantity: number;
    product: Product;
    createdAt: string;
    updatedAt: string;
  }>>> => api.put(`/cart/${productId}`, { quantity }),

  removeFromCart: (productId: number): Promise<AxiosResponse<ApiResponse<null>>> =>
    api.delete(`/cart/${productId}`),

  clearCart: (): Promise<AxiosResponse<ApiResponse<null>>> =>
    api.delete('/cart'),

  syncCart: (localCartItems: Array<{ productId: number; quantity: number }>): Promise<AxiosResponse<ApiResponse<{
    items: Array<{
      id: number;
      userId: number;
      productId: number;
      quantity: number;
      product: Product;
      createdAt: string;
      updatedAt: string;
    }>;
    totalItems: number;
    totalPrice: number;
    totalDeposit: number;
    grandTotal: number;
    syncResults: {
      added: any[];
      updated: any[];
      errors: any[];
    };
  }>>> => api.put('/cart/sync', { localCartItems }),

  validateCartStock: (): Promise<AxiosResponse<ApiResponse<{
    validItems: Array<{
      id: number;
      userId: number;
      productId: number;
      quantity: number;
      product: Product;
      createdAt: string;
      updatedAt: string;
    }>;
    stockIssues?: Array<{
      productId: number;
      productName: string;
      requestedQuantity?: number;
      availableStock?: number;
      issue: string;
    }>;
    canProceedToCheckout: boolean;
  }>>> => api.get('/cart/validate'),
};

// Admin API
export const adminAPI = {
  login: (credentials: {
    email: string;
    password: string;
  }): Promise<AxiosResponse<AuthResponse>> =>
    api.post('/admin/login', credentials),

  getDashboardStats: (): Promise<AxiosResponse<ApiResponse<{
    stats: DashboardStats;
    recentOrders: Order[];
    monthlyRevenue: MonthlyRevenue[];
  }>>> =>
    api.get('/admin/dashboard'),

  getUsers: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    role?: string;
    isActive?: boolean;
  }): Promise<AxiosResponse<PaginatedResponse<User>>> =>
    api.get('/admin/users', { params }),

  getAllOrders: (params?: {
    page?: number;
    limit?: number;
    status?: string;
    search?: string;
  }): Promise<AxiosResponse<PaginatedResponse<Order>>> =>
    api.get('/admin/orders', { params }),

  getOrderDetails: (id: number): Promise<AxiosResponse<ApiResponse<{ order: Order }>>> =>
    api.get(`/admin/orders/${id}`),

  updateOrderStatus: (id: number, data: {
    status: string;
    adminNotes?: string;
    changeReason?: string;
  }): Promise<AxiosResponse<ApiResponse<{
    orderId: number;
    previousStatus: string;
    newStatus: string;
    changedBy: number;
    timestamp: string;
  }>>> =>
    api.put(`/admin/orders/${id}/status`, data),
  updateOrderPaymentStatus: (id: number, data: {
    paymentStatus: string;
    paymentMethod?: string;
    amountPaid?: number;
    amountDue?: number;
    adminNotes?: string;
    changeReason?: string;
  }): Promise<AxiosResponse<ApiResponse<{
    orderId: number;
    previousPaymentStatus: string;
    newPaymentStatus: string;
    paymentMethod: string;
    amountPaid: number;
    amountDue: number;
    changedBy: number;
    timestamp: string;
  }>>> =>
    api.put(`/admin/orders/${id}/payment-status`, data),

  getOrderStatusHistory: (id: number): Promise<AxiosResponse<ApiResponse<{
    orderId: number;
    statusHistory: Array<{
      id: number;
      orderId: number;
      previousStatus: string | null;
      newStatus: string;
      changeReason: string | null;
      notes: string | null;
      ipAddress: string | null;
      userAgent: string | null;
      createdAt: string;
      updatedAt: string;
      changedByUser?: {
        id: number;
        name: string;
        email: string;
        role: string;
      };
    }>;
  }>>> =>
    api.get(`/admin/orders/${id}/status-history`),

  getUserDetails: (id: number): Promise<AxiosResponse<ApiResponse<{
    user: User;
    stats: {
      totalOrders: number;
      totalSpent: number;
    };
  }>>> =>
    api.get(`/admin/users/${id}`),

  updateUserStatus: (id: number, data: {
    isActive: boolean;
  }): Promise<AxiosResponse<ApiResponse<{ user: User }>>> =>
    api.put(`/admin/users/${id}/status`, data),

  createUser: (userData: {
    name: string;
    email: string;
    password: string;
    phone: string;
    address?: string;
    city?: string;
    pincode?: string;
    role: 'admin' | 'customer';
  }): Promise<AxiosResponse<ApiResponse<{ user: User }>>> =>
    api.post('/admin/users', userData),
};

// Admin Products API
export const adminProductsAPI = {
      createProduct: (productData: {
        name: string;
        description?: string;
        type: 'gallon' | 'bottle';
        size: string;
        capacity: number;
        price: number;
        deposit?: number;
        categoryId?: number;
        brand?: string;
        stock?: number;
        image?: string;
      }): Promise<AxiosResponse<ApiResponse<{ product: Product }>>> =>
        api.post('/admin/products', productData),

  getAllProducts: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    type?: string;
    category?: string;
    isActive?: boolean;
  }): Promise<AxiosResponse<PaginatedResponse<Product>>> =>
    api.get('/admin/products', { params }),

  getProductDetails: (id: number): Promise<AxiosResponse<ApiResponse<{ product: Product }>>> =>
    api.get(`/admin/products/${id}`),

      updateProduct: (id: number, productData: {
        name?: string;
        description?: string;
        type?: 'gallon' | 'bottle';
        size?: string;
        capacity?: number;
        price?: number;
        deposit?: number;
        categoryId?: number;
        brand?: string;
        stock?: number;
        isActive?: boolean;
        image?: string;
      }): Promise<AxiosResponse<ApiResponse<{ product: Product }>>> =>
        api.put(`/admin/products/${id}`, productData),

  toggleProductStatus: (id: number): Promise<AxiosResponse<ApiResponse<{ product: Product }>>> =>
    api.patch(`/admin/products/${id}/toggle-status`),

  restoreProduct: (id: number): Promise<AxiosResponse<ApiResponse<{ product: Product }>>> =>
    api.patch(`/admin/products/${id}/restore`),

  permanentDeleteProduct: (id: number): Promise<AxiosResponse<ApiResponse<null>>> =>
    api.delete(`/admin/products/${id}/permanent`, {
      data: { confirm: 'DELETE' }
    }),
};

// Upload API
export const uploadAPI = {
  uploadProductImage: (file: File): Promise<AxiosResponse<ApiResponse<{
    url: string;
    key: string;
    fileName: string;
    size: number;
    mimetype: string;
  }>>> => {
    const formData = new FormData();
    formData.append('image', file);
    return api.post('/upload/product', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  updateProductImage: (productId: number, file: File): Promise<AxiosResponse<ApiResponse<{
    product: Product;
    imageUrl: string;
    imageKey: string;
  }>>> => {
    const formData = new FormData();
    formData.append('image', file);
    return api.put(`/upload/product/${productId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  deleteProductImage: (productId: number): Promise<AxiosResponse<ApiResponse<{
    product: Product;
  }>>> =>
    api.delete(`/upload/product/${productId}`),
};

// Category API
export const categoryAPI = {
  getCategories: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    isActive?: boolean;
  }): Promise<AxiosResponse<PaginatedResponse<Category>>> =>
    api.get('/categories', { params }),

  getActiveCategories: (): Promise<AxiosResponse<ApiResponse<{
    categories: Category[];
  }>>> =>
    api.get('/categories/active'),

  getCategoryDetails: (id: number): Promise<AxiosResponse<ApiResponse<{
    category: Category;
  }>>> =>
    api.get(`/categories/${id}`),

  createCategory: (categoryData: {
    name: string;
    description?: string;
    category_image?: string;
    color?: string;
    sortOrder?: number;
  }): Promise<AxiosResponse<ApiResponse<{ category: Category }>>> =>
    api.post('/categories', categoryData),

  updateCategory: (id: number, categoryData: {
    name?: string;
    description?: string;
    category_image?: string;
    color?: string;
    sortOrder?: number;
    isActive?: boolean;
  }): Promise<AxiosResponse<ApiResponse<{ category: Category }>>> =>
    api.put(`/categories/${id}`, categoryData),

  toggleCategoryStatus: (id: number): Promise<AxiosResponse<ApiResponse<{ category: Category }>>> =>
    api.patch(`/categories/${id}/toggle-status`),

  restoreCategory: (id: number): Promise<AxiosResponse<ApiResponse<{ category: Category }>>> =>
    api.patch(`/categories/${id}/restore`),

  permanentDeleteCategory: (id: number): Promise<AxiosResponse<ApiResponse<null>>> =>
    api.delete(`/categories/${id}/permanent`, {
      data: { confirm: 'DELETE' }
    }),
};

// OTP API
export const otpAPI = {
  // Registration OTP
  sendRegistrationOTP: (email: string): Promise<AxiosResponse<ApiResponse<{ email: string; expiresIn: number }>>> =>
    api.post('/otp/send-registration-otp', { email }),

  verifyRegistrationOTP: (email: string, otp: string): Promise<AxiosResponse<ApiResponse<null>>> =>
    api.post('/otp/verify-registration-otp', { email, otp }),

  resendRegistrationOTP: (email: string): Promise<AxiosResponse<ApiResponse<{ email: string; expiresIn: number }>>> =>
    api.post('/otp/resend-registration-otp', { email }),

  // Login OTP
  sendLoginOTP: (email: string): Promise<AxiosResponse<ApiResponse<{ email: string; expiresIn: number }>>> =>
    api.post('/otp/send-login-otp', { email }),

  verifyLoginOTP: (email: string, otp: string): Promise<AxiosResponse<ApiResponse<{ userId: number; email: string; name: string }>>> =>
    api.post('/otp/verify-login-otp', { email, otp }),

  resendLoginOTP: (email: string): Promise<AxiosResponse<ApiResponse<{ email: string; expiresIn: number }>>> =>
    api.post('/otp/resend-login-otp', { email }),
};

// Email Service API (for additional email functionality)
export const emailAPI = {
  // Send order confirmation email
  sendOrderConfirmation: (email: string, name: string, orderData: any): Promise<AxiosResponse<ApiResponse<null>>> =>
    api.post('/email/send-order-confirmation', { email, name, orderData }),

  // Send order status update email
  sendOrderStatusUpdate: (email: string, name: string, orderData: any): Promise<AxiosResponse<ApiResponse<null>>> =>
    api.post('/email/send-order-status-update', { email, name, orderData }),

  // Send password reset OTP email
  sendPasswordResetOTP: (email: string, name: string, otp: string): Promise<AxiosResponse<ApiResponse<null>>> =>
    api.post('/email/send-password-reset-otp', { email, name, otp }),
};

// Contact API
export const contactAPI = {
  // Submit contact form
  submitContactForm: (contactData: {
    name: string;
    email: string;
    message: string;
  }): Promise<AxiosResponse<ApiResponse<{
    id: number;
    name: string;
    email: string;
    submittedAt: string;
  }>>> =>
    api.post('/contact/submit', contactData),

  // Get all contact submissions (admin only)
  getAllContacts: (params?: {
    page?: number;
    limit?: number;
    status?: string;
    search?: string;
  }): Promise<AxiosResponse<PaginatedResponse<{
    id: number;
    name: string;
    email: string;
    message: string;
    domain: string | null;
    ipAddress: string | null;
    status: 'new' | 'read' | 'replied' | 'closed';
    adminNotes: string | null;
    repliedAt: string | null;
    repliedBy: number | null;
    createdAt: string;
    updatedAt: string;
  }>>> =>
    api.get('/contact', { params }),

  // Get single contact submission (admin only)
  getContactById: (id: number): Promise<AxiosResponse<ApiResponse<{
    id: number;
    name: string;
    email: string;
    message: string;
    domain: string | null;
    ipAddress: string | null;
    userAgent: string | null;
    status: 'new' | 'read' | 'replied' | 'closed';
    adminNotes: string | null;
    repliedAt: string | null;
    repliedBy: number | null;
    createdAt: string;
    updatedAt: string;
  }>>> =>
    api.get(`/contact/${id}`),

  // Update contact status (admin only)
  updateContactStatus: (id: number, data: {
    status?: 'new' | 'read' | 'replied' | 'closed';
    adminNotes?: string;
  }): Promise<AxiosResponse<ApiResponse<{
    id: number;
    name: string;
    email: string;
    message: string;
    domain: string | null;
    ipAddress: string | null;
    userAgent: string | null;
    status: 'new' | 'read' | 'replied' | 'closed';
    adminNotes: string | null;
    repliedAt: string | null;
    repliedBy: number | null;
    createdAt: string;
    updatedAt: string;
  }>>> =>
    api.patch(`/contact/${id}/status`, data),

  // Delete contact submission (admin only)
  deleteContact: (id: number): Promise<AxiosResponse<ApiResponse<null>>> =>
    api.delete(`/contact/${id}`),
};

// Password Reset API
export const passwordResetAPI = {
  // Request password reset
  forgotPassword: (email: string): Promise<AxiosResponse<ApiResponse<null>>> =>
    api.post('/password-reset/forgot', { email }),

  // Reset password with token
  resetPassword: (data: {
    token: string;
    password: string;
    confirmPassword: string;
  }): Promise<AxiosResponse<ApiResponse<null>>> =>
    api.post('/password-reset/reset', data),

  // Verify reset token
  verifyResetToken: (token: string): Promise<AxiosResponse<ApiResponse<{
    user: {
      id: number;
      name: string;
      email: string;
    };
    expiresAt: string;
  }>>> =>
    api.get(`/password-reset/verify/${token}`),

  // Get password reset history (admin only)
  getPasswordResetHistory: (params?: {
    page?: number;
    limit?: number;
    userId?: number;
  }): Promise<AxiosResponse<PaginatedResponse<{
    id: number;
    userId: number;
    token: string;
    expiresAt: string;
    used: boolean;
    ipAddress: string | null;
    userAgent: string | null;
    createdAt: string;
    updatedAt: string;
    user: {
      id: number;
      name: string;
      email: string;
    };
  }>>> =>
    api.get('/password-reset/history', { params }),
};

// Visitor API
export const visitorAPI = {
  incrementVisitor: (data?: {
    timestamp?: string;
    userAgent?: string;
    referrer?: string;
    page?: string;
    site?: string;
  }): Promise<AxiosResponse<ApiResponse<{
    uniqueVisitors: number;
    totalPageViews: number;
    isNewVisitor: boolean;
    visitorId: number | null;
  }>>> =>
    api.post('/visitors/increment', { site: 'h2go', ...data }),

  getVisitorCount: (site?: string): Promise<AxiosResponse<ApiResponse<{
    uniqueVisitors: number;
    totalPageViews: number;
    count: number;
  }>>> =>
    api.get(`/visitors/count?site=${site || 'h2go'}`),

  getVisitorStats: (site?: string): Promise<AxiosResponse<ApiResponse<{
    totalCount: number;
    todayCount: number;
    weekCount: number;
    monthCount: number;
    recentVisitors: Array<{
      id: number;
      timestamp: string;
      ipAddress: string | null;
      userAgent: string | null;
      referrer: string | null;
      page: string | null;
      site: string;
    }>;
  }>>> =>
    api.get(`/visitors/stats?site=${site || 'h2go'}`),

  deleteVisitor: (id: number): Promise<AxiosResponse<ApiResponse<{
    count: number;
  }>>> =>
    api.delete(`/visitors/${id}`),
};

export default api;
